import { MutableRefObject, useCallback, useRef } from 'react';
import SwiperCore from 'swiper';

// Pauses a Swiper's autoplay while it's scrolled out of view. `canStart` (optional) gates resuming.
export const useAutoplayInView = <T extends HTMLElement = HTMLDivElement>(
  swiperRef: MutableRefObject<SwiperCore | undefined>,
  canStart?: () => boolean
): ((node: T | null) => void) => {
  const canStartRef = useRef(canStart);
  canStartRef.current = canStart;

  const observerRef = useRef<IntersectionObserver | null>(null);

  return useCallback(
    (node: T | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!node || typeof IntersectionObserver === 'undefined') return;

      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          const autoplay = swiperRef.current?.autoplay;

          if (!autoplay) return;

          if (entry.isIntersecting) {
            if (!canStartRef.current || canStartRef.current()) autoplay.start();
          } else {
            autoplay.stop();
          }
        },
        { threshold: 0 }
      );

      observerRef.current.observe(node);
    },
    [swiperRef]
  );
};
